import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import {
  Table,
  Button,
  Card,
  Input,
  Typography,
  Switch,
  Modal,
  Form,
  Space,
  message,
  Tag,
  Popconfirm,
  List,
  Tooltip,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CommandTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [paramModalVisible, setParamModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [paramForm] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  // Charger les templates
  const loadTemplates = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('command_types')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      message.error('Erreur lors du chargement des templates');
    } else {
      setTemplates(data);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleOpenModal = (template = null) => {
    setEditingTemplate(template);
    if (template) {
      form.setFieldsValue({
        ...template,
        parameters: template.parameters || [],
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        parameters: [],
        active: true,
      });
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingTemplate(null);
    form.resetFields();
  };

  const handleAddParameter = () => {
    const parameters = form.getFieldValue('parameters') || [];
    paramForm.resetFields();
    setParamModalVisible(true);
  };

  const handleParamSubmit = (values) => {
    const parameters = form.getFieldValue('parameters') || [];
    form.setFieldsValue({
      parameters: [...parameters, values],
    });
    setParamModalVisible(false);
    paramForm.resetFields();
  };

  const handleRemoveParameter = (index) => {
    const parameters = form.getFieldValue('parameters');
    form.setFieldsValue({
      parameters: parameters.filter((_, i) => i !== index),
    });
  };


  const handleDelete = async (template) => {
    try {
      const { error } = await supabase
        .from('command_types')
        .delete()
        .eq('id', template.id);

      if (error) throw error;

      message.success('Template supprimé avec succès');
      loadTemplates();
    } catch (error) {
      message.error('Erreur lors de la suppression du template');
      console.error('Error:', error);
    }
  };

  const getParameterHelp = (parameters) => {
    if (!parameters?.length) return '';
    return parameters.map(p => `{${p.name}}`).join(', ');
  };

  const isDefaultTemplate = (code) => {
    return defaultTemplates.some(template => template.code === code);
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Message',
      dataIndex: 'message_template',
      key: 'message_template',
      width: 300,
      render: (text, record) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ 
            maxWidth: '280px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            <Tooltip title={text.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}>
              {text}
            </Tooltip>
          </div>
          {record.parameters?.length > 0 && (
            <div style={{ marginTop: '4px' }}>
              <Space size={[0, 4]} wrap>
                {record.parameters.map((param, index) => (
                  <Tag key={index} color={param.required ? 'red' : 'green'}>
                    {param.name}
                  </Tag>
                ))}
              </Space>
            </div>
          )}
        </Space>
      ),
    },
    {
      title: 'Paramètres',
      dataIndex: 'parameters',
      key: 'parameters',
      render: (parameters) => (
        <Space wrap>
          {(parameters || []).map((param, index) => (
            <Tag key={index} color="blue">
              {param.name}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'active',
      key: 'active',
      render: (active) => (
        <Tag icon={active ? <CheckCircleOutlined /> : <CloseCircleOutlined />} 
            color={active ? 'success' : 'error'}>
          {active ? 'Actif' : 'Inactif'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          />
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer ce template ?"
            onConfirm={() => handleDelete(record)}
            okText="Oui"
            cancelText="Non"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} 
              disabled={record.code === 'MSGLIVREUR' || record.code === 'MSGSATISFACTION' || record.code === 'MSGPHOTO' || record.code === 'MSGPROMO'}  // Désactiver la suppression pour les templates par défaut
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const defaultTemplates = [
    {
      code: 'MSGLIVREUR',
      name: 'Message de livraison',
      description: 'Envoyé le jour de la livraison',
      message_template: `Bonjour,
      Votre commande N°{order_id} a été expédiée !
      Voici votre numéro de suivi : {delivery_number}
      Bonne réception 🎁`,
      parameters: [
        { name: 'order_id', label: 'N° de commande', required: false },
        { name: 'delivery_number', label: 'N° de suivi', required: false },
        { name: 'price', label: 'Prix', required: false },
        { name: 'phone', label: 'N° de telephone', required: false },
        { name: 'address', label: 'Adresse', required: false },
        { name: 'delivery_date', label: 'Date de livraison', required: false },
      ],
    },
    {
      code: 'MSGSATISFACTION',
      name: 'Message de satisfaction',
      description: 'Envoyé 2 jours après la livraison',
      message_template: `Bonjour,
      J'espère que vous avez bien reçu votre commande et que tout vous plaít ! 😊
      N'hésitez pas à me faire un retour sur vos articles.
      Bonne journée !`,
      parameters: [
        { name: 'order_id', label: 'N° de commande', required: false },
        { name: 'delivery_number', label: 'N° de suivi', required: false },
        { name: 'price', label: 'Prix', required: false },
        { name: 'phone', label: 'N° de telephone', required: false },
        { name: 'address', label: 'Adresse', required: false },
        { name: 'delivery_date', label: 'Date de livraison', required: false },
      ],
    },
    {
      code: 'MSGPHOTO',
      name: 'Demande de photo',
      description: 'Envoyé 5 jours après la livraison',
      message_template: `Bonjour,
      J'espère que vous profitez bien de vos articles ! 
      Si vous le souhaitez, vous pouvez partager une photo de vous avec vos articles.
      Cela m'aiderait beaucoup à améliorer mes services 📸
      Merci d'avance !`,
      parameters: [
        { name: 'order_id', label: 'N° de commande', required: false },
        { name: 'delivery_number', label: 'N° de suivi', required: false },
        { name: 'price', label: 'Prix', required: false },
        { name: 'phone', label: 'N° de telephone', required: false },
        { name: 'address', label: 'Adresse', required: false },
        { name: 'delivery_date', label: 'Date de livraison', required: false },
      ],
    },
    {
      code: 'MSGPROMO',
      name: 'Code promo fidélité',
      description: 'Envoyé 12 jours après la livraison',
      message_template: `Bonjour,
      Pour vous remercier de votre confiance, voici un code promo de -10% valable sur votre prochaine commande : MERCI10
      À très bientôt ! 🛍️`,
      parameters: [
        { name: 'order_id', label: 'N° de commande', required: false },
        { name: 'delivery_number', label: 'N° de suivi', required: false },
        { name: 'price', label: 'Prix', required: false },
        { name: 'phone', label: 'N° de telephone', required: false },
        { name: 'address', label: 'Adresse', required: false },
        { name: 'delivery_date', label: 'Date de livraison', required: false },
      ],
    },
    {
      code: 'NEWORDER',
      name: 'Nouvelle commande',
      description: 'Envoyé lorsque une nouvelle commande est créée',
      message_template: `Bonjour, votre commande d'un total de {price} F CFA a été créée avec succès.
      Merci pour votre commande !`,
      parameters: [
        { name: 'order_id', label: 'N° de commande', required: false },
        { name: 'delivery_number', label: 'N° de suivi', required: false },
        { name: 'price', label: 'Prix', required: false },
        { name: 'phone', label: 'N° de telephone', required: false },
        { name: 'address', label: 'Adresse', required: false },
        { name: 'delivery_date', label: 'Date de livraison', required: false },
      ],
    },
    {
      code: 'SHIFTEDORDER',
      name: 'Message de livraison reportée',
      description: 'Envoyé lorsque la livraison est reportée',
      message_template: `Bonjour, nos livreurs étant déjà sortis,
      votre commande d'un total de {price} F CFA livré à {address} est donc programmée pour demain.
      Merci pour votre commande !`,
      parameters: [
        { name: 'order_id', label: 'N° de commande', required: false },
        { name: 'delivery_number', label: 'N° de suivi', required: false },
        { name: 'price', label: 'Prix', required: false },
        { name: 'phone', label: 'N° de telephone', required: false },
        { name: 'address', label: 'Adresse', required: false },
        { name: 'delivery_date', label: 'Date de livraison', required: false },
      ],
    }
  ];
  
  const createDefaultTemplates = async () => {

    const confirmed = window.confirm(
      "Voulez-vous créer les templates par défaut pour la gestion des commandes Shopify ? (Livraison, Satisfaction, Photo, Promo)"
    );
    if (confirmed) {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('user', user);
      try {
        setIsLoading(true);
        for (const template of defaultTemplates) {
          const { data, error } = await supabase
            .from('command_types')
            .select('*')
            .eq('code', template.code)
            .eq('user_id', user.id)
            .single();
          console.log('data', data);
          console.log('error', error);

          if (!data && error) {
            await supabase.from('command_types').insert({
              ...template,
              user_id: user.id
            });
          }
        }
        loadTemplates();
        message.success('Templates créés avec succès !');
      } catch (error) {
        console.error('Erreur lors de la création des templates:', error);
        message.error('Erreur lors de la création des templates');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSave = async (values) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (editingTemplate) {
        // Modification
        const updatedTemplate = {
          ...editingTemplate,
          ...values,
          parameters: values.parameters || editingTemplate.parameters || []  // Garder les paramètres existants ou utiliser les nouveaux
        };
        
        const { error } = await supabase
          .from('command_types')
          .update(updatedTemplate)
          .eq('id', editingTemplate.id);

        if (error) throw error;
        message.success('Template modifié avec succès');
      } else {
        // Création
        const { error } = await supabase
          .from('command_types')
          .insert({
            ...values,
            parameters: values.parameters || [],  // S'assurer que parameters existe
            user_id: user.id
          });

        if (error) throw error;
        message.success('Template créé avec succès');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      message.error('Erreur lors de la sauvegarde');
    }
  };

  return (
    <div className="p-6">
      <Card>
        <div className="flex justify-between items-center mb-6">
          <Title level={4}>Templates de Commandes WhatsApp</Title>
          <div className="space-x-2">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              Nouveau Template
            </Button>
            <Button
              onClick={createDefaultTemplates}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              Créer templates Shopify
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
          style={{ overflowX: 'auto' }}
        />

        <Modal
          title={editingTemplate ? 'Modifier le Template' : 'Nouveau Template'}
          open={modalVisible}
          onCancel={handleCloseModal}
          footer={null}
          width={800}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={{
              active: true,
              parameters: [],
            }}
          >
            <Form.Item
              name="code"
              label="Code"
              rules={[{ required: true, message: 'Le code est requis' }]}
            >
              <Input placeholder="ex: RELANCE" />
            </Form.Item>

            <Form.Item
              name="name"
              label="Nom"
              rules={[{ required: true, message: 'Le nom est requis' }]}
            >
              <Input placeholder="ex: Relance client" />
            </Form.Item>

            <Form.Item
              label="Paramètres"
              required={false}
            >
              <Form.List name="parameters">
                {(fields, { add, remove }) => (
                  <>
                    <List
                      size="small"
                      bordered
                      dataSource={fields}
                      renderItem={(field, index) => (
                        <List.Item
                          actions={[
                            !isDefaultTemplate(form.getFieldValue('code')) && (
                              <Button
                                key="delete"
                                type="link"
                                danger
                                onClick={() => handleRemoveParameter(index)}
                              >
                                Supprimer
                              </Button>
                            ),
                          ].filter(Boolean)}
                        >
                          <Space>
                            <Form.Item
                              {...field}
                              name={[field.name, 'name']}
                              noStyle
                            >
                              <Input disabled style={{ width: 120 }} />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              name={[field.name, 'label']}
                              noStyle
                            >
                              <Input disabled style={{ width: 120 }} />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              name={[field.name, 'required']}
                              valuePropName="checked"
                              noStyle
                            >
                              <Switch
                                disabled
                                checkedChildren="Requis"
                                unCheckedChildren="Optionnel"
                              />
                            </Form.Item>
                          </Space>
                        </List.Item>
                      )}
                    />
                    {!isDefaultTemplate(form.getFieldValue('code')) && (
                      <Button
                        type="dashed"
                        onClick={() => handleAddParameter()}
                        style={{ width: '100%', marginTop: '10px' }}
                        icon={<PlusOutlined />}
                      >
                        Ajouter un paramètre
                      </Button>
                    )}
                  </>
                )}
              </Form.List>
            </Form.Item>

            <Form.Item
              name="message_template"
              label="Message Template"
              rules={[{ required: true, message: 'Le message est requis' }]}
              help={
                <Text type="secondary">
                  Paramètres disponibles: {getParameterHelp(form.getFieldValue('parameters'))}
                </Text>
              }
            >
              <TextArea
                rows={4}
                placeholder="ex: Bonjour! Nous avons remarqué votre intérêt pour {article}..."
              />
            </Form.Item>

            <Form.Item name="active" valuePropName="checked" label="Actif">
              <Switch />
            </Form.Item>

            <Form.Item className="text-right">
              <Space>
                <Button onClick={handleCloseModal}>Annuler</Button>
                <Button type="primary" htmlType="submit">
                  {editingTemplate ? 'Modifier' : 'Créer'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Ajouter un paramètre"
          open={paramModalVisible}
          onCancel={() => setParamModalVisible(false)}
          footer={null}
        >
          <Form
            form={paramForm}
            layout="vertical"
            onFinish={handleParamSubmit}
          >
            <Form.Item
              name="name"
              label="Nom du paramètre"
              rules={[
                { required: true, message: 'Le nom est requis' },
                {
                  pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/,
                  message: 'Le nom doit commencer par une lettre et ne contenir que des lettres, chiffres et _',
                },
              ]}
              tooltip="Ce nom sera utilisé dans le template entre accolades. Ex: {nom}"
            >
              <Input placeholder="ex: prix" />
            </Form.Item>

            <Form.Item
              name="label"
              label="Label"
              rules={[{ required: true, message: 'Le label est requis' }]}
              tooltip="Label affiché dans l'interface"
            >
              <Input placeholder="ex: Prix du produit" />
            </Form.Item>

            <Form.Item
              name="required"
              valuePropName="checked"
              label="Requis"
            >
              <Switch />
            </Form.Item>

            <Form.Item className="text-right">
              <Space>
                <Button onClick={() => setParamModalVisible(false)}>
                  Annuler
                </Button>
                <Button type="primary" htmlType="submit">
                  Ajouter
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default CommandTemplates;
